import { Box, Button, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import NotifySong from "../../assets/notify.wav";
import { playNotify } from "../../services/audio/audioEngine";

// Icone volume-up inline (evita dependencia de @mui/icons-material).
const VolumeIcon = ({ size = 140, color = "#fff" }: { size?: number; color?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    aria-hidden="true"
  >
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

type UnlockState = "checking" | "unlocked" | "locked";

const UNLOCK_TS_KEY = "panel@audioUnlockedAt";

// Overlay fullscreen que pede 1 clique humano para liberar autoplay.
//
// IMPORTANTE — o que "preservar o status" significa:
// O Chrome zera o user-gesture a cada reload. Nao tem como guardar
// permissao em localStorage. Mas o Chrome MANTEM um Media Engagement
// Index (MEI) por origem que cresce a cada audio tocado com sucesso.
// Depois de alguns plays bem-sucedidos, o Chrome passa a permitir
// autoplay automaticamente, mesmo sem clique.
//
// Esta versao detecta isso na montagem: tenta um play unmuted no
// volume 0 (silencioso, mas sujeito a politica de autoplay). Se o
// Chrome ja libera, pula o overlay. Senao, mostra ate o operador
// clicar uma vez. Apos o operador clicar algumas vezes (uma por reload
// nas primeiras vezes), o MEI sobe e o overlay para de aparecer.
export default function AudioUnlock() {
  const theme = useTheme();
  const [state, setState] = useState<UnlockState>("checking");

  useEffect(() => {
    let cancelled = false;

    const isPWA =
      (typeof window !== "undefined" &&
        window.matchMedia?.("(display-mode: standalone)").matches) ||
      (typeof window !== "undefined" &&
        (window.navigator as any).standalone === true);
    if (isPWA) {
      setState("unlocked");
      return;
    }

    // API moderna do Chrome (100+): consulta direta da policy.
    const policy = (navigator as any).getAutoplayPolicy?.("mediaelement");
    if (policy === "allowed") {
      setState("unlocked");
      return;
    }

    // Probe: tenta autoplay unmuted no volume 0. Se Chrome permitir
    // (PWA, flag de policy ou MEI ja suficiente), passa silencioso.
    // Senao, rejeita com NotAllowedError e mantemos o overlay.
    const probe = new Audio(NotifySong);
    probe.muted = false;
    probe.volume = 0;
    probe.preload = "auto";
    probe
      .play()
      .then(() => {
        try {
          probe.pause();
          probe.currentTime = 0;
        } catch {
          // ignore
        }
        if (!cancelled) setState("unlocked");
      })
      .catch(() => {
        if (!cancelled) setState("locked");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleUnlock = () => {
    // Toca o notify.wav como confirmacao audivel de que o som funciona.
    // Fire-and-forget: ja consome o user gesture e tambem incrementa
    // o MEI da origem para reduzir necessidade de cliques futuros.
    playNotify({ callId: "audio-unlock" }).catch(() => {
      // Telemetry ja captura erros; o gesto em si libera o autoplay.
    });

    // Prime tambem o speechSynthesis com utterance silenciosa.
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        const u = new SpeechSynthesisUtterance(" ");
        u.volume = 0;
        u.lang = "pt-BR";
        window.speechSynthesis.speak(u);
      } catch {
        // ignore — best effort
      }
    }

    // Marca timestamp do desbloqueio (uso futuro / debug). Nao garante
    // permissao no proximo reload, mas eh sinal de que MEI deve crescer.
    try {
      localStorage.setItem(UNLOCK_TS_KEY, String(Date.now()));
    } catch {
      // localStorage pode falhar em modo privado; nao bloqueia
    }

    setState("unlocked");
  };

  // Enquanto checa, nao renderiza nada — assim o painel atras nao pisca
  // com overlay flashando antes da deteccao resolver.
  if (state !== "locked") return null;

  return (
    <Box
      onClick={handleUnlock}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleUnlock();
      }}
      sx={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.92)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        zIndex: 99999,
        cursor: "pointer",
        gap: 4,
        userSelect: "none",
      }}
    >
      <VolumeIcon size={140} color={theme.palette.background.default} />
      <Typography
        component="h1"
        sx={{
          fontSize: "3.5rem",
          fontWeight: 600,
          color: theme.palette.background.default,
          textAlign: "center",
          px: 4,
          textShadow: "5px 5px 10px rgba(0, 0, 0, 0.25)",
        }}
      >
        TOQUE PARA INICIAR O PAINEL
      </Typography>
      <Typography
        sx={{
          fontSize: "1.4rem",
          color: "rgba(255, 255, 255, 0.75)",
          textAlign: "center",
          maxWidth: 720,
          px: 4,
          lineHeight: 1.5,
        }}
      >
        O navegador exige uma interação inicial para autorizar a reprodução
        de áudio. Após este toque, todas as chamadas vão tocar
        automaticamente.
      </Typography>
      <Button
        variant="contained"
        size="large"
        onClick={(e) => {
          e.stopPropagation();
          handleUnlock();
        }}
        sx={{
          fontSize: "2.2rem",
          fontWeight: 600,
          padding: "1.5rem 5rem",
          mt: 3,
          backgroundColor: theme.palette.primary.dark,
          "&:hover": { backgroundColor: theme.palette.primary.main },
        }}
      >
        INICIAR
      </Button>
    </Box>
  );
}
